import { Box, Chip, Stack, Typography } from '@mui/material';
import RecipeImageTile from '../../../components/shared/RecipeImageTile';
import AllergenChips from '../../../components/shared/AllergenChips';
import { RecipeV2 } from '../types';

interface RecipeCardProps {
  recipe: RecipeV2;
  onClick: () => void;
}

/** Library card for the Recipe Bank grid - image tile, name, category chip, a handful of
 * allergen chips (overflow-capped) and a dietary indicator if the recipe carries any
 * Suitable/Certified dietary attribute worth calling out. */
const RecipeCard = ({ recipe, onClick }: RecipeCardProps) => {
  const displayName = recipe.customerFacingName ?? recipe.description;
  const dietaryHighlights = recipe.dietaryAttributes
    .filter((a) => a.status === 'Suitable' || a.status === 'Certified')
    .slice(0, 2);

  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        boxShadow: '0 1px 2px 1px #1018280D',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
      }}
    >
      <RecipeImageTile
        imageUrl={recipe.imageUrl}
        alt={displayName}
        height={160}
        sx={{ borderRadius: 0, border: 'none' }}
      />
      <Box sx={{ p: 2 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}>
          {displayName}
        </Typography>
        <Stack direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}>
          <Chip label={recipe.primaryCategory}
            size="small"
            variant="outlined" />
          {dietaryHighlights.map((d) => (
            <Chip key={d.code}
              label={d.name}
              size="small"
              color="success"
              variant="outlined" />
          ))}
        </Stack>
        <AllergenChips
          allergens={recipe.allergenSummary}
          max={4}
          size="small"
          sx={{ mt: 1.5 }}
        />
      </Box>
    </Box>
  );
};

export default RecipeCard;
